import type {
  Discipline,
  Feat,
  FightingTechnique,
  FightingTier,
  RemovedFeat,
  RuleEntry,
  Technique,
} from "@/domain/content";

import techniquesJson from "../../content/techniques.json";
import webTechniquesJson from "../../content/techniques-web.json";
import fightingJson from "../../content/fighting-techniques.json";
import featsJson from "../../content/feats.json";
import removedJson from "../../content/feats-removed.json";
// NOTE: content/rules-gmbinder.json (EN) and content/rules-gmbinder-tr.json
// (TR) are deliberately NOT imported here — they are the only per-locale pair
// in content/ (~350 kB each) and only one is ever shown. They are fetched per
// locale through loadRuleDocument() below so Rollup ships each as its own
// on-demand chunk. Everything else carries both locales inline and stays eager.
import fateJson from "../../content/rules-fate.json";

/**
 * Content modules are read-only source data (Game Data / Game Rules layers).
 * They are validated once at load so the rest of the app can trust the types.
 * Narrowing happens here rather than at each call site.
 */
function oneOf<T extends string>(value: string, allowed: readonly T[], what: string): T {
  if (!(allowed as readonly string[]).includes(value)) {
    throw new Error(`Unexpected ${what}: ${value}`);
  }
  return value as T;
}

function ruleLevel(value: number): 1 | 2 | 3 {
  if (value !== 1 && value !== 2 && value !== 3) {
    throw new Error(`Unexpected rule level: ${value}`);
  }
  return value;
}

/** Disciplines observed in the extracted source data. */
const DISCIPLINES = [
  "Universal",
  "Waterbending",
  "Earthbending",
  "Firebending",
  "Airbending",
  "Bloodbending",
  "Lavabending",
  "Metalbending",
  "Lightningbending",
  "Combustionbending",
  "Healing",
] as const satisfies readonly Discipline[];

const FIGHTING_TIERS = ["basic", "trained", "mastered"] as const satisfies readonly FightingTier[];

const techniques: Technique[] = (techniquesJson as Technique[]).map((t) => ({
  ...t,
  discipline: oneOf(t.discipline, DISCIPLINES, "discipline"),
}));

const fightingTechniques: FightingTechnique[] = (fightingJson as FightingTechnique[]).map((t) => ({
  ...t,
  tier: oneOf(t.tier, FIGHTING_TIERS, "fighting tier"),
}));

const feats: Feat[] = featsJson as Feat[];

const removedFeats: RemovedFeat[] = removedJson as RemovedFeat[];

function loadRules(json: unknown): RuleEntry[] {
  if (!Array.isArray(json)) {
    throw new Error(`Rule document is not an array (${typeof json})`);
  }
  return (json as RuleEntry[]).map((r) => ({ ...r, level: ruleLevel(r.level) }));
}

const rulesFate: RuleEntry[] = loadRules(fateJson);

// ------------------------------------------------------- lazy ATLA 5e Homebrew book
//
// The ATLA 5e Homebrew rulebook exists as two files — the source English extract and
// its Turkish translation — with identical ids and order, and the UI only ever
// shows ONE of them. Shipping both statically cost ~350 kB of every download,
// so they are fetched per locale instead: a dynamic import lets Rollup emit
// each JSON as its own chunk, and the cache below makes every reader
// (`getRules`) stay synchronous.
type RuleLocale = "tr" | "en";

/** Locales whose rulebook has finished loading (source order preserved). */
const ruleDocuments = new Map<RuleLocale, RuleEntry[]>();
/** In-flight loads, so concurrent callers share one fetch. */
const ruleDocumentLoads = new Map<RuleLocale, Promise<RuleEntry[]>>();
/** Notified when a locale's rulebook lands (React re-renders off this). */
const ruleDocumentListeners = new Set<() => void>();

/** Stable empty answer — `[]` that keeps referential identity between renders. */
const NO_RULES: RuleEntry[] = [];

/** A JSON module dynamic import hands the payload over as `default`. */
function jsonDefault(mod: unknown): unknown {
  return mod !== null && typeof mod === "object" && "default" in mod
    ? (mod as { default: unknown }).default
    : mod;
}

/**
 * Fetch one locale's ATLA 5e Homebrew rulebook and cache it. Safe to call often:
 * the first call per locale starts the chunk fetch, later ones resolve with
 * the cached entries. Failures are NOT cached, so a caller may retry.
 */
export function loadRuleDocument(locale: RuleLocale): Promise<RuleEntry[]> {
  const loaded = ruleDocuments.get(locale);
  if (loaded) return Promise.resolve(loaded);
  const inflight = ruleDocumentLoads.get(locale);
  if (inflight) return inflight;

  const load: Promise<RuleEntry[]> = (
    locale === "tr"
      ? import("../../content/rules-gmbinder-tr.json")
      : import("../../content/rules-gmbinder.json")
  )
    .then((mod) => {
      const entries = loadRules(jsonDefault(mod));
      ruleDocuments.set(locale, entries);
      ruleDocumentLoads.delete(locale);
      for (const listener of ruleDocumentListeners) listener();
      return entries;
    })
    .catch((error: unknown) => {
      ruleDocumentLoads.delete(locale);
      throw error;
    });

  ruleDocumentLoads.set(locale, load);
  return load;
}

/** Whether `getRules(_, locale)` would return real entries right now. */
export function isRuleDocumentLoaded(locale: RuleLocale): boolean {
  return ruleDocuments.has(locale);
}

/** Subscribe to "a locale's rulebook arrived" (useSyncExternalStore-friendly). */
export function subscribeRuleDocument(listener: () => void): () => void {
  ruleDocumentListeners.add(listener);
  return () => ruleDocumentListeners.delete(listener);
}

/**
 * ATLA 5e Homebrew entries loaded so far — EN is the source document; TR is its
 * translation (same ids/order), so either one gives the true entry count.
 */
function loadedRuleDocument(): RuleEntry[] {
  return ruleDocuments.get("en") ?? ruleDocuments.get("tr") ?? NO_RULES;
}

/** Bending techniques, source order preserved. */
export function getTechniques(): Technique[] {
  return techniques;
}

/** Bending techniques for one discipline, source order preserved. */
export function getTechniquesByDiscipline(discipline: Discipline): Technique[] {
  return techniques.filter((t) => t.discipline === discipline);
}

export function getTechnique(id: string): Technique | undefined {
  return techniques.find((t) => t.id === id);
}

// --------------------------------------------------- researched techniques
//
// content/techniques-web.json holds techniques that do NOT come from the two
// supplied PDFs: canon sub-arts the PDFs never print text for (sandbending,
// plantbending, lightning redirection, chi-blocking…), written by the
// web-research pass. They are kept in their own file — and behind their own
// accessors — so the source extract stays exactly as the extractor produced it
// and every consumer can tell the two apart (`researched: true`).

/** A technique whose text is homebrew-from-research rather than PDF-extracted. */
export interface ResearchedTechnique extends Omit<Technique, 'discipline'> {
  /** Free-form: research may name an art the source documents do not have. */
  discipline: string;
  /** Balance intent, in the researcher's words. */
  notes?: string;
  /** Fate framing for tables running the other system. */
  fate?: {
    aspectHint?: string;
    stunt?: string;
    sourceUrls?: string[];
  };
}

const researchedTechniques: ResearchedTechnique[] = (
  webTechniquesJson as ResearchedTechnique[]
).filter((t) => typeof t.id === 'string' && t.id !== '' && typeof t.discipline === 'string');

/** Pool entry used by pickers/print: source and researched look the same. */
export interface PoolTechnique extends Omit<Technique, 'discipline'> {
  discipline: string;
  /** true when this came from content/techniques-web.json, not the PDFs. */
  researched: boolean;
}

function toPool(technique: Technique | ResearchedTechnique, researched: boolean): PoolTechnique {
  return { ...technique, researched };
}

/** Every technique of a discipline, source first, researched after. */
export function getPoolForDiscipline(discipline: string): PoolTechnique[] {
  const fromSource = techniques
    .filter((t) => t.discipline === discipline)
    .map((t) => toPool(t, false));
  const fromWeb = researchedTechniques
    .filter((t) => t.discipline.toLowerCase() === discipline.toLowerCase())
    .map((t) => toPool(t, true));
  return [...fromSource, ...fromWeb];
}

/** The whole pool — used by search and print. */
export function getPoolTechniques(): PoolTechnique[] {
  return [
    ...techniques.map((t) => toPool(t, false)),
    ...researchedTechniques.map((t) => toPool(t, true)),
  ];
}

/** Lookup across both files; source wins on an id collision. */
export function getAnyTechnique(id: string): PoolTechnique | undefined {
  const source = techniques.find((t) => t.id === id);
  if (source) return toPool(source, false);
  const web = researchedTechniques.find((t) => t.id === id);
  return web ? toPool(web, true) : undefined;
}

export function getResearchedTechniques(): ResearchedTechnique[] {
  return researchedTechniques;
}

/** Disciplines that exist only in the researched file (art chips, filters). */
export function getResearchedDisciplines(): string[] {
  const known = new Set<string>(DISCIPLINES);
  const extra = new Set<string>();
  for (const technique of researchedTechniques) {
    if (!known.has(technique.discipline)) extra.add(technique.discipline);
  }
  return [...extra];
}

/** Fighting techniques for one tier, source order preserved. */
export function getFightingByTier(tier: FightingTier): FightingTechnique[] {
  return fightingTechniques.filter((t) => t.tier === tier);
}

export function getFightingTechnique(id: string): FightingTechnique | undefined {
  return fightingTechniques.find((t) => t.id === id);
}

/** Official feats (source truth; removed ones listed separately below). */
export function getFeats(): Feat[] {
  return feats;
}

export function getFeat(id: string): Feat | undefined {
  return feats.find((f) => f.id === id);
}

/** Feats flagged as removed at the table (Annex C), with GM notes. */
export function getRemovedFeats(): RemovedFeat[] {
  return removedFeats;
}

export function getRemovedFeatByName(nameEn: string): RemovedFeat | undefined {
  return removedFeats.find((r) => r.name.en === nameEn);
}

/**
 * Rule sections from either document, filtered by document id + locale.
 *
 * Synchronous on purpose: the Fate book ships inline, and the ATLA 5e Homebrew book
 * returns `[]` until loadRuleDocument(locale) resolved — never the other
 * locale's text, never a throw. Callers that must show the list (or print it)
 * kick the load off and use isRuleDocumentLoaded/subscribeRuleDocument to know
 * when to fill in.
 */
export function getRules(
  document: "gmbinder" | "fate",
  locale: RuleLocale = "en",
): RuleEntry[] {
  if (document === "gmbinder") {
    return ruleDocuments.get(locale) ?? NO_RULES;
  }
  return rulesFate;
}

export function getRule(id: string): RuleEntry | undefined {
  return [...loadedRuleDocument(), ...rulesFate].find((r) => r.id === id);
}

/**
 * Content inventory for the cockpit stats card. `ruleEntries` counts the GM
 * Binder book that is currently loaded (source order: gmbinder + fate) — the
 * Turkish file is a translation of the same entries, not extra ones.
 */
export function contentStats(): {
  techniques: number;
  researchedTechniques: number;
  fightingTechniques: number;
  feats: number;
  removedFeats: number;
  ruleEntries: number;
} {
  return {
    techniques: techniques.length,
    researchedTechniques: researchedTechniques.length,
    fightingTechniques: fightingTechniques.length,
    feats: feats.length,
    removedFeats: removedFeats.length,
    ruleEntries: loadedRuleDocument().length + rulesFate.length,
  };
}
